import { ref } from 'vue'
import { defineStore } from 'pinia'

export type NotificationType = 'error' | 'warning' | 'success' | 'info'

export interface Notification {
  title: string
  message: string
  type: NotificationType
  isVisible: boolean
  statusCode?: number
  duration?: number // auto-dismiss after ms (0 = manual dismiss only)
}

export const useNotificationStore = defineStore('notification', () => {
  const notification = ref<Notification>({
    title: '',
    message: '',
    type: 'info',
    isVisible: false,
    duration: 0
  })

  const setNotification = (
    title: string,
    message: string,
    type: NotificationType = 'info',
    statusCode?: number,
    duration?: number
  ) => {
    notification.value = {
      title,
      message,
      type,
      statusCode,
      isVisible: true,
      duration: duration ?? 0
    }
  }

  const clearNotification = () => {
    notification.value.isVisible = false
  }

  // Convenience methods
  const error = (title: string, message: string, statusCode?: number, duration?: number) => {
    setNotification(title, message, 'error', statusCode, duration ?? 0)
  }

  const warning = (title: string, message: string, duration?: number) => {
    setNotification(title, message, 'warning', undefined, duration ?? 5000)
  }

  const success = (title: string, message: string, duration?: number) => {
    setNotification(title, message, 'success', undefined, duration ?? 3000)
  }

  const info = (title: string, message: string, duration?: number) => {
    setNotification(title, message, 'info', undefined, duration ?? 3000)
  }

  return {
    notification,
    setNotification,
    clearNotification,
    error,
    warning,
    success,
    info
  }
})
